import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Minus, Plus, Maximize, Download } from "lucide-react";

const PdfViewer = lazy(async () => {
  const m = await import("@/components/shared/data-display/PdfViewer");
  return { default: m.PdfViewer };
});
import { SIGNATURE_PROBE_BYTES, detectMimeFromBytes } from "@/utils/mimeDetection";
import { useMimeProbe } from "@/hooks/useMimeProbe";

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_DEFAULT = 1;
const MAX_BASE_WIDTH = 720;
/**
 * Avoid showing a spinner for fast Range responses; only render the loading
 * state if verification hasn't completed within this budget. Tuned for GCS
 * p95 (~80ms in pre/prod).
 */
const VERIFICATION_LOADING_DELAY_MS = 200;

interface DocumentViewerProps {
  url: string;
  contentType: string;
  filename?: string;
  onDownload?: () => void;
}

const useVerifiedContentType = (
  url: string,
  declared: string,
): { effectiveType: string; isVerifying: boolean } => {
  /**
   * Defense in depth: even after Layers 1-4 land documents.content_type may
   * still lag for in-flight uploads (the classifier hasn't self-healed yet)
   * or for assets uploaded by a third party. We always sniff the actual
   * bytes via a one-shot Range request and override the declared type when
   * the signature disagrees. On any failure (CORS, 416, network) we
   * silently fall back to the declared value — never break the happy path.
   */
  const [verifiedType, setVerifiedType] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(true);
  const probeMimeBytes = useMimeProbe();

  useEffect(() => {
    let cancelled = false;
    setVerifiedType(null);
    setIsVerifying(true);

    const probe = async () => {
      const bytes = await probeMimeBytes(url, SIGNATURE_PROBE_BYTES);
      if (cancelled) return;
      if (bytes) setVerifiedType(detectMimeFromBytes(bytes));
      setIsVerifying(false);
    };

    probe();
    return () => {
      cancelled = true;
    };
  }, [url, probeMimeBytes]);

  return {
    effectiveType: verifiedType ?? declared,
    isVerifying,
  };
};

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  url,
  contentType,
  filename,
  onDownload,
}) => {
  const { effectiveType, isVerifying } = useVerifiedContentType(url, contentType);
  const isImage = effectiveType.startsWith("image/");

  // Suppress the loading state when verification finishes within the budget
  // so the viewer feels instant for the typical case.
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (!isVerifying) {
      setShowLoading(false);
      return;
    }
    const t = window.setTimeout(() => setShowLoading(true), VERIFICATION_LOADING_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [isVerifying]);

  const [scale, setScale] = useState(ZOOM_DEFAULT);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const zoomIn = useCallback(() => setScale((s) => Math.min(s + ZOOM_STEP, ZOOM_MAX)), []);
  const zoomOut = useCallback(() => setScale((s) => Math.max(s - ZOOM_STEP, ZOOM_MIN)), []);
  const resetZoom = useCallback(() => setScale(ZOOM_DEFAULT), []);

  const percentage = Math.round(scale * 100);
  const baseWidth = containerWidth > 0 ? Math.min(containerWidth, MAX_BASE_WIDTH) : 0;
  const pageWidth = baseWidth > 0 ? baseWidth * scale : undefined;

  return (
    <div className="relative w-full h-full flex flex-col bg-bg-tertiary rounded-lg overflow-hidden">
      {/* Toolbar — bottom right */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center bg-bg-primary/90 backdrop-blur-sm border border-border-secondary rounded-md shadow-sm divide-x divide-border-secondary">
        <button
          onClick={zoomOut}
          disabled={scale <= ZOOM_MIN}
          className="flex items-center justify-center w-7 h-7 text-text-secondary hover:bg-bg-tertiary transition-colors rounded-l-md disabled:opacity-40 disabled:cursor-not-allowed"
          title="Zoom out"
        >
          <Minus size={13} />
        </button>

        <span className="flex items-center justify-center h-7 px-2 text-xs font-medium text-text-tertiary select-none min-w-[3rem]">
          {percentage}%
        </span>

        <button
          onClick={zoomIn}
          disabled={scale >= ZOOM_MAX}
          className="flex items-center justify-center w-7 h-7 text-text-secondary hover:bg-bg-tertiary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Zoom in"
        >
          <Plus size={13} />
        </button>

        <button
          onClick={resetZoom}
          className="flex items-center justify-center w-7 h-7 text-text-secondary hover:bg-bg-tertiary transition-colors"
          title="Fit to width"
        >
          <Maximize size={13} />
        </button>

        {onDownload && (
          <button
            onClick={onDownload}
            className="flex items-center justify-center w-7 h-7 text-text-secondary hover:bg-bg-tertiary transition-colors rounded-r-md"
            title="Download"
          >
            <Download size={13} />
          </button>
        )}
      </div>

      {/* Document content — scrollable, centered when content < container */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4">
        <div
          className="min-h-full flex flex-col items-center"
          style={{
            minWidth: pageWidth && pageWidth > containerWidth ? pageWidth : "100%",
          }}
        >
          {showLoading ? (
            <div className="flex-1 flex items-center justify-center text-xs text-text-tertiary">
              Verificando documento…
            </div>
          ) : isImage ? (
            <img
              src={url}
              alt={filename}
              style={{ width: pageWidth }}
              className="h-auto rounded-lg shadow-lg"
            />
          ) : (
            pageWidth && (
              <Suspense fallback={null}>
                <PdfViewer url={url} title={filename} pageWidth={pageWidth} />
              </Suspense>
            )
          )}
        </div>
      </div>
    </div>
  );
};
